import { Eye, FileText } from "lucide-react";

import { Button } from "@/components/ui/button";

type FileDisplayCardProps = {
  /** The name of the file to display */
  fileName: string;
  /** The size of the file formatted as a string (e.g., "80MB") */
  fileSize?: string;
  /** Whether the preview button should be disabled */
  disabled?: boolean;
  /** Callback when preview button is clicked */
  onPreview: () => void;
};

/**
 * FileDisplayCard component
 *
 * A reusable card component for displaying file information with a preview button.
 * Uses a grid layout with icon, file details, and preview action.
 * Commonly used in UpdateCaseModal and CaseOverview to show uploaded case files.
 *
 * @param fileName - The name of the file to display
 * @param fileSize - The size of the file formatted as a string
 * @param disabled - Whether the preview button should be disabled
 * @param onPreview - Callback when preview button is clicked
 *
 * @example
 * ```tsx
 * <FileDisplayCard
 *   fileName="Medical Report 2024.docx"
 *   fileSize="80MB"
 *   onPreview={() => handlePreview()}
 * />
 * ```
 */
const FileDisplayCard = ({
  fileName,
  fileSize,
  disabled = false,
  onPreview,
}: FileDisplayCardProps) => {
  return (
    <div className="w-full max-w-2xl rounded-xl bg-background-white p-3">
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
        <div className="shrink-0 rounded-full bg-primary-50 p-2">
          <FileText className="size-5 text-primary" />
        </div>

        <div className="min-w-0 overflow-hidden">
          <p className="truncate text-sm font-bold">{fileName}</p>
          {fileSize && <p className="text-xs text-foreground-muted">{fileSize}</p>}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onPreview}
          disabled={disabled}
          className="flex items-center gap-1 p-2 text-primary"
        >
          <Eye className="size-4" />
          Preview
        </Button>
      </div>
    </div>
  );
};

export default FileDisplayCard;
